import { Box, Typography } from '@mui/material';
import AIBasePopover from 'components/AIBasePopover';
import { AISuggestionPopoverProps } from './AISuggestionPopover.props';
import {
  commentaryItemSx,
  commentaryTitleSx,
  documentColorSx,
  referenceTypeColorSx,
} from './SuggestedDocumentReference.styles';

const CLASSIFICATION_DISPLAY_MAP: Record<string, string> = {
  CAP_TABLE: 'Cap Table',
  BALANCE_SHEET: 'Balance Sheet',
  INCOME_STATEMENT: 'Income Statement',
  CASH_FLOW_STATEMENT: 'Cash Flow Statement',
  OTHER: 'Other',
};

const AISuggestionPopover = ({
  documentReferenceId: _documentReferenceId,
  aiClassification,
  commentary: commentaryStrings,
  referencedIn,
  defaultExpanded = false,
  title,
  subtitle,
  expandedContent,
  acceptLabel = 'Accept',
  declineLabel = 'Dismiss',
  onAccept,
  onDecline,
  children,
}: AISuggestionPopoverProps) => {
  const classification = aiClassification;
  const classificationResult = classification?.classification_result || 'Document';
  const displayName = CLASSIFICATION_DISPLAY_MAP[classificationResult] || classificationResult;

  const suggestedReference = referencedIn?.[0];
  const referenceType = suggestedReference?.reference_object_type || displayName;

  // Normalize commentary from either source
  const normalizedCommentary: { content: string }[] = classification?.commentary
    ?? commentaryStrings?.map(s => ({ content: s }))
    ?? [];

  const resolvedSubtitle = subtitle ?? (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      Add this{' '}
      <Typography component="span" variant="body2" sx={documentColorSx}>
        Document
      </Typography>{' '}
      to{' '}
      <Typography component="span" variant="body2" sx={referenceTypeColorSx}>
        {referenceType}
      </Typography>
    </Typography>
  );

  const expandedBody = expandedContent ?? (
    <Box>
      <Typography variant="body2" sx={commentaryTitleSx}>
        Why this suggestion?
      </Typography>
      {normalizedCommentary.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
          {normalizedCommentary.map((note, index) => (
            <Typography
              key={index}
              component="li"
              variant="body2"
              color="text.secondary"
              sx={commentaryItemSx}
            >
              {note.content}
            </Typography>
          ))}
        </Box>
      )}

      {classification?.confidence != null && (
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary">
            Confidence: {Math.round(classification.confidence * 100)}%
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <AIBasePopover
      title={title ?? 'Suggested Reference'}
      subtitle={resolvedSubtitle}
      acceptLabel={acceptLabel}
      declineLabel={declineLabel}
      onAccept={onAccept}
      onDecline={onDecline}
      expandedBody={expandedBody}
      trigger="hover"
      defaultExpanded={defaultExpanded}
    >
      {({ onMouseEnter, onMouseLeave, onClick, ariaDescribedBy }) =>
        children({
          onMouseEnter,
          onMouseLeave,
          onClick,
          ariaDescribedBy,
        })
      }
    </AIBasePopover>
  );
};

export default AISuggestionPopover;
